import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
  inject,
} from '@angular/core';
import { NgTemplateOutlet, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MakeAndModel, MakeModelFilterData, TreeNode } from '../../../models/make-model.model';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { Observable, Subject, Subscription } from 'rxjs';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { MakeModelFilterService } from '../../../services/filters/make-model-filter.service';
import { TranslocoDirective, TranslocoPipe } from '@jsverse/transloco';

@Component({
  standalone: true,
  selector: 'app-make-model-filter',
  templateUrl: './make-model-filter.component.html',
  styleUrl: './make-model-filter.component.scss',
  imports: [NgbModule, NgTemplateOutlet, CommonModule, FormsModule, TranslocoDirective, TranslocoPipe],
})
export class MakeAndModelFilterComponent implements OnInit, OnDestroy {
  private makeandModelService = inject(MakeModelFilterService);
  private errorService = inject(ErrorHandlerService);

  @Input() disabled: boolean = false;
  @Input() filter: MakeModelFilterData = { makesAndModels: [] };
  @Input() accountFilterChanged$: Observable<number[]>;
  @Output() filterChanged: EventEmitter<MakeModelFilterData> =
    new EventEmitter<MakeModelFilterData>();

  isLoading: boolean = false;
  searchTerm: string = '';

  // Tree data
  makeModelTree: TreeNode[] = [];
  filteredTree: TreeNode[] = [];
  selectedItems: TreeNode[] = [];
  selectedItemsCount: number = 0;

  private accountFilterSubscription: Subscription;
  private cancelSubject: Subject<void> = new Subject<void>();
  selectedAccounts: Array<number> = [];

  ngOnInit(): void {
    // Subscribe to account changes
    if (this.accountFilterChanged$) {
      this.accountFilterSubscription = this.accountFilterChanged$.subscribe(
        (accountIds) => {
          this.selectedAccounts = accountIds ?? [];
          // Clear selected items when accounts change
          this.selectedItems = [];
          this.filter.makesAndModels = [];
          this.updateSelectedCount();
          this.loadMakeModelData();
        }
      );
    }
  }

  ngOnDestroy(): void {
    if (this.accountFilterSubscription) {
      this.accountFilterSubscription.unsubscribe();
    }
  }

  async loadMakeModelData(): Promise<void> {
    this.isLoading = true;
    try {
      this.cancelSubject.next();
      const response = await this.makeandModelService.getMakeModelList(
        this.selectedAccounts,
        this.cancelSubject
      );
      this.makeModelTree = this.buildMakeModelTree(response.data ?? []);
      this.filteredTree = this.makeModelTree;
      this.applyFilter(response.data ?? []);
      this.isLoading = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        },
      });
    }
  }

  buildMakeModelTree(data: MakeAndModel[]): TreeNode[] {
    const makeMap = new Map<string, TreeNode>();
    let autoId = 1;

    data.forEach((item) => {
      const make = item.make?.trim();
      const model = item.model?.trim();

      if (!make) return;

      if (!makeMap.has(make)) {
        makeMap.set(make, {
          id: autoId++,
          name: make,
          checked: false,
          expanded: false,
          children: [],
          makeModelData: { make },
        });
      }

      if (model) {
        makeMap.get(make)!.children.push({
          id: autoId++,
          parentId: makeMap.get(make)!.id,
          name: model,
          checked: false,
          expanded: false,
          children: [],
          makeModelData: { make, model },
        });
      }
    });

    return Array.from(makeMap.values());
  }

  searchMakeModel(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value;
    if (searchTerm) {
      this.filteredTree = this.searchTreeItems(this.makeModelTree, searchTerm);
    } else {
      this.filteredTree = this.makeModelTree;
    }

    // Reapply checked state
    this.filteredTree.forEach((item) => {
      this.applyToggle(item);
    });
  }

  searchTreeItems(items: TreeNode[], searchTerm: string): TreeNode[] {
    return items.reduce((acc: TreeNode[], item) => {
      const parentMatches = item.name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchedChildren = this.searchTreeItems(
        item.children,
        searchTerm.toLowerCase()
      );

      if (parentMatches || matchedChildren.length > 0) {
        acc.push({
          ...item,
          // If parent matches, include ALL children. Otherwise, only include matched children
          children: parentMatches ? item.children : matchedChildren,
        });
      }

      return acc;
    }, []);
  }

  onCheckboxChange(item: TreeNode): void {
    this.toggleCheckBox(item);
    this.updateFilter();
    this.updateSelectedCount();
  }

  private toggleCheckBox(item: TreeNode): void {
    const index = this.selectedItems.findIndex((x) => x.id === item.id);

    if (index === -1) {
      this.selectedItems.push(item);
      item.checked = true;
    } else {
      this.selectedItems.splice(index, 1);
      item.checked = false;
    }

    // Toggle all children
    item.children.forEach((child) => {
      this.toggleChildren(child, item.checked);
    });
  }

  private toggleChildren(item: TreeNode, toggle: boolean): void {
    if (toggle) {
      if (!this.selectedItems.find((x) => x.id === item.id)) {
        this.selectedItems.push(item);
        item.checked = true;
      }
    } else {
      const index = this.selectedItems.findIndex((x) => x.id === item.id);
      if (index !== -1) {
        this.selectedItems.splice(index, 1);
      }
      item.checked = false;
    }

    item.children.forEach((child) => {
      this.toggleChildren(child, item.checked);
    });
  }

  private applyToggle(item: TreeNode): void {
    item.checked = !!this.selectedItems.find((x) => x.id === item.id);
    item.children.forEach((child) => {
      this.applyToggle(child);
    });
  }

  private applyFilter(data: MakeAndModel[]): void {
    if (this.filter.makesAndModels && this.filter.makesAndModels.length > 0) {
      // Rebuild tree to match selected items
      this.selectedItems = [];
      const tempTree = this.buildMakeModelTree(data);

      this.filter.makesAndModels.forEach((filterItem: MakeAndModel) => {
        this.findAndSelectNode(tempTree, filterItem);
      });

      this.updateSelectedCount();
    }
  }

  private findAndSelectNode(nodes: TreeNode[], makeModel: MakeAndModel): void {
    for (const node of nodes) {
      if (node.makeModelData) {
        const matches =
          node.makeModelData.make === makeModel.make &&
          (makeModel.model
            ? node.makeModelData.model === makeModel.model
            : true);

        if (matches) {
          if (!this.selectedItems.find((x) => x.id === node.id)) {
            this.selectedItems.push(node);
            node.checked = true;
          }
        }
      }

      if (node.children.length > 0) {
        this.findAndSelectNode(node.children, makeModel);
      }
    }
  }

  private updateFilter(): void {
    this.filter.makesAndModels = this.selectedItems
      .filter((item) => item.makeModelData)
      .map((item) => item.makeModelData!);

    this.filterChanged.emit(this.filter);
  }

  private updateSelectedCount(): void {
    this.selectedItemsCount = this.selectedItems.length;
  }
}
